import React, {useEffect, useRef, useState} from 'react';
import post from '../utils/post.js';
import FormText from './FormText.js';
import FormTextArea from './FormTextArea.js';
import EditableProfilePhoto from './EditableProfilePhoto.js';
import ConfirmPopup from './ConfirmPopup.js';
import usePopupAnimation from '../utils/usePopupAnimation.js';

function splitPhone(phoneValue) {
	const digits = String(phoneValue || '').replace(/\D/g, '');

	if (digits.length > 9) {
		return {
			prefix: digits.slice(0, digits.length - 9),
			phone: digits.slice(-9)
		};
	}

	return {
		prefix: digits === '' ? '420' : '420',
		phone: digits
	};
}

export default function InstructorUserEdit({showEdit, closeEdit, refreshList, data}) {
	const popupAnimationMs = 300;
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState('');
	const [firstName, setFirstName] = useState('');
	const [firstNameError, setFirstNameError] = useState('');
	const [lastName, setLastName] = useState('');
	const [lastNameError, setLastNameError] = useState('');
	const [nickname, setNickname] = useState('');
	const [age, setAge] = useState('');
	const [myweight, setMyweight] = useState('');
	const [target, setTarget] = useState('');
	const [prefix, setPrefix] = useState('420');
	const [phone, setPhone] = useState('');
	const [note, setNote] = useState('');
	const [formError, setFormError] = useState('');
	const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	const firstNameRef = useRef();
	const lastNameRef = useRef();
	const prefixRef = useRef();
	const phoneRef = useRef();
	const noteRef = useRef();
	const {shouldRender, isClosing} = usePopupAnimation(showEdit, popupAnimationMs);

	useEffect(() => {
		if (!showEdit || !data) {
			return;
		}

		const phoneParts = splitPhone(data.PHONE);

		setEmail(data.EMAIL || '');
		setFirstName(data.FIRSTNAME || '');
		setFirstNameError('');
		setLastName(data.LASTNAME || '');
		setLastNameError('');
		setNickname(data.NICKNAME || '');
		setAge(data.AGE || '');
		setMyweight(data.WEIGHT || '');
		setTarget(data.TARGET || '');
		setPrefix(phoneParts.prefix || '420');
		setPhone(phoneParts.phone || '');
		setNote(data.NOTE || '');
		setFormError('');
		setProfilePhotoUrl(data.PROFILE_PHOTO_URL || '');
		setShowConfirmPopup(false);
	}, [showEdit, data]);

	const handleConfirmPopup = () => {
		setShowConfirmPopup(true);
	};

	const closeConfirmPopup = () => {
		if (loading) {
			return;
		}

		setShowConfirmPopup(false);
	};

	const deactivateBinding = () => {
		if (!data?.ID) {
			return;
		}

		setLoading(true);
		setFormError('');

		post('deactivateInstructorUser', {id: data.ID})
			.then(() => {
				setShowConfirmPopup(false);
				refreshList();
				closeEdit();
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setFormError(error?.response?.data?.error || 'Zrušení vazby se nepodařilo.');
				setShowConfirmPopup(false);
				setLoading(false);
			});
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		setFirstNameError('');
		setLastNameError('');
		setFormError('');

		let hasError = false;

		if (!firstName) {
			setFirstNameError('povinné');
			firstNameRef.current.focus();
			hasError = true;
		}

		if (!lastName) {
			if (!hasError) {
				lastNameRef.current.focus();
			}
			setLastNameError('povinné');
			hasError = true;
		}

		if (hasError) {
			return;
		}

		setLoading(true);

		post('saveInstructorUserEdit', {id: data.ID, firstName, lastName, nickname, age, myweight, target, prefix, phone, note})
			.then(() => {
				refreshList();
				closeEdit();
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setFormError(error?.response?.data?.error || 'Uložení uživatele se nepodařilo.');
				setLoading(false);
			});
	};

	const displayName = `${lastName} ${firstName}${nickname ? ` (${nickname})` : ''}`.trim() || email;

	return shouldRender ? (
		<>
			<ConfirmPopup
				showConfirmPopup={showConfirmPopup}
				closeAnimationMs={popupAnimationMs}
				closeConfirmPopup={closeConfirmPopup}
				infoHeader="Zrušení vazby"
				infoMain="Opravdu zrušit vazbu na klienta? Je možné ho znou přidat přes tlačítko Nový pomocí emailu nebo qr kódu."
				footerChildren={(
					<button type="button" className="button red mr_2" onClick={deactivateBinding} disabled={loading}>
						Zrušit vazbu
					</button>
				)}
			/>
			<div className={`popup${isClosing ? ' popup--closing' : ''}`}>
				<div className={`popup_content instructor-user-new__content${isClosing ? ' popup_content--closing' : ''}`}>
					<form method="POST" onSubmit={handleSubmit}>
					<div className="popup_content_in_head">
						<div className="popup_content_in_head_in">Upravit klienta</div>
						<img alt="" className="popup_content_in_head_in-close" onClick={closeEdit} />
					</div>
					<div className="popup_content_in_body instructor-user-new__body">
						<div className="popup_content_in_body_in instructor-user-new__body-in">
							<div className="instructor-user-new__photo-panel">
								<EditableProfilePhoto
									photoUrl={profilePhotoUrl}
									displayName={displayName}
									canEditPhoto={true}
									uploadUrl="/rest/uploadInstructorProfilePhoto"
									deleteUrl="/rest/deleteInstructorProfilePhoto"
									entityKey={data?.ID}
									onPhotoChange={(nextPhotoUrl) => setProfilePhotoUrl(nextPhotoUrl || '')}
									avatarWidth="6rem"
									avatarHeight="6rem"
								/>
								<div className="instructor-user-new__photo-copy">
									<div className="instructor-user-new__photo-title">Profilová fotka</div>
									<div className="instructor-user-new__photo-hint">Klikni na fotku pro výběr a ořez ze zařízení nebo z fotoaparátu.</div>
								</div>
							</div>

							<FormText type="text" label="EMAIL" value={email} setValue={setEmail} error="" disabled />

							<div className="instructor-user-new__grid instructor-user-new__grid--two">
								<div>
									<FormText type="text" label="JMÉNO" value={firstName} setValue={setFirstName} ref={firstNameRef} error={firstNameError} />
								</div>
								<div>
									<FormText type="text" label="PŘÍJMENÍ" value={lastName} setValue={setLastName} ref={lastNameRef} error={lastNameError} />
								</div>
							</div>

							<div className="instructor-user-new__grid instructor-user-new__grid--three">
								<div>
									<FormText type="text" label="PŘEZDÍVKA" value={nickname} setValue={setNickname} error="" />
								</div>
								<div>
									<FormText type="text" label="PŘEDV." value={prefix} setValue={setPrefix} ref={prefixRef} error="" maxLength={4} disabled />
								</div>
								<div>
									<FormText type="text" label="TELEFON" value={phone} setValue={setPhone} ref={phoneRef} error="" maxLength={9} disabled />
								</div>
							</div>

							<div className="instructor-user-new__grid instructor-user-new__grid--two">
								<div>
									<FormText type="number" label="VĚK" value={age} setValue={setAge} error="" />
								</div>
								<div>
									<FormText type="number" label="HMOTNOST" value={myweight} setValue={setMyweight} error="" />
								</div>
							</div>

								<FormTextArea type="text" label="CÍL KLIENTA" value={target} setValue={setTarget} error="" height="8rem" />

							<FormTextArea type="text" label="POZNÁMKA" value={note} setValue={setNote} ref={noteRef} error="" height="20rem" />

							{formError !== '' ? <div className="instructor-user-new__error">{formError}</div> : null}
						</div>
					</div>
					<div className="popup_content_in_foot">
						<div className="popup_content_in_foot_in">
							<input type="submit" name="button_save" value="Uložit" className="button green mr_2" disabled={loading} />
							<button type="button" className="button red mr_2" onClick={handleConfirmPopup} disabled={loading}>Zrušit vazbu</button>
							<button type="button" className="button" onClick={closeEdit}>Zpět</button>
						</div>
					</div>
					</form>
				</div>
			</div>
		</>
	) : '';
}